import React, { useRef, useState, useCallback } from 'react';
import { useInlineEdit } from '@/contexts/InlineEditContext';
import { Check, X } from 'lucide-react';
import { sanitizeHTML } from '@/utils/sanitize';

const EditableText = ({ pageType, pageId, field, children, as: Tag = 'span', className = '', richText = false, ...rest }) => {
  const { editMode, saveField } = useInlineEdit();
  const editorRef = useRef(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [originalHTML, setOriginalHTML] = useState('');

  const handleClick = useCallback((e) => {
    if (!editMode || editing) return;
    e.stopPropagation();
    e.preventDefault();
    setEditing(true);
    if (editorRef.current) {
      setOriginalHTML(editorRef.current.innerHTML);
      editorRef.current.contentEditable = true;
      editorRef.current.focus();
      // Place cursor at end
      const range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, [editMode, editing]);

  const handleSave = useCallback(async () => {
    if (!editorRef.current) return;
    setSaving(true);
    const rawValue = richText ? editorRef.current.innerHTML : editorRef.current.textContent;
    const newValue = richText ? sanitizeHTML(rawValue) : rawValue;
    const success = await saveField(pageType, pageId, field, newValue);
    if (success) {
      setEditing(false);
      editorRef.current.contentEditable = false;
    } else {
      // Revert on failure
      editorRef.current.innerHTML = originalHTML;
      setEditing(false);
      editorRef.current.contentEditable = false;
    }
    setSaving(false);
  }, [pageType, pageId, field, richText, saveField, originalHTML]);

  const handleCancel = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = originalHTML;
      editorRef.current.contentEditable = false;
    }
    setEditing(false);
  }, [originalHTML]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') handleCancel();
    if (e.key === 'Enter' && !richText && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
  }, [handleCancel, handleSave, richText]);

  if (!editMode) {
    // Normal render — no edit wrappers
    if (richText) {
      return <Tag className={className} dangerouslySetInnerHTML={{ __html: sanitizeHTML(typeof children === 'string' ? children : '') }} {...rest} />;
    }
    return <Tag className={className} {...rest}>{children}</Tag>;
  }

  // Edit mode render
  return (
    <span className="relative inline-block group/edit" style={{ display: Tag === 'div' || Tag === 'p' || Tag === 'h1' || Tag === 'h2' || Tag === 'h3' ? 'block' : undefined }}>
      <Tag
        ref={editorRef}
        className={`${className} ${editing ? 'outline outline-2 outline-[#E63946] outline-offset-2 bg-[#E63946]/5' : 'cursor-pointer hover:outline hover:outline-1 hover:outline-[#E63946]/40 hover:outline-offset-2 hover:bg-[#E63946]/5'} transition-all`}
        onClick={handleClick}
        onKeyDown={editing ? handleKeyDown : undefined}
        suppressContentEditableWarning
        data-testid={`editable-${field}`}
        {...rest}
      >
        {richText
          ? <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(typeof children === 'string' ? children : '') }} />
          : children
        }
      </Tag>

      {/* Save/Cancel toolbar */}
      {editing && (
        <span data-cms-skip className="absolute -top-9 right-0 flex items-center gap-1 z-50 animate-in fade-in slide-in-from-bottom-2 duration-150">
          <button
            data-cms-skip
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1 px-2.5 py-1 bg-[#E63946] text-white text-xs font-medium hover:bg-[#d32f3f] transition-colors shadow-lg"
            data-testid="inline-save-btn"
          >
            <Check className="w-3 h-3" />
            {saving ? '...' : 'Save'}
          </button>
          <button
            data-cms-skip
            onClick={handleCancel}
            className="flex items-center gap-1 px-2.5 py-1 bg-[#1a1a1a] text-gray-300 text-xs font-medium hover:bg-[#2a2a2a] border border-white/10 transition-colors shadow-lg"
            data-testid="inline-cancel-btn"
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      )}
    </span>
  );
};

export default EditableText;
